import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { Check, Loader2, ArrowLeft, Sun, Moon, Menu, CreditCard, User, Facebook, Languages, X } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import SEO from '../../components/SEO';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import LanguageSwitcher from '../../components/LanguageSwitcher';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function Pricing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentPlan, setCurrentPlan] = useState<string | null>(null);
  const [loading, setLoading] = useState<string | null>(null);
  const { theme, toggleTheme } = useTheme();

  // Promotion Valentine's Day - Expire le 28 février 2026 à minuit
  const isPromoActive = new Date() < new Date('2026-03-01T00:00:00');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    checkAuth();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setIsLoggedIn(!!user);
    if (user) {
      // Fetch all active subscriptions
      const { data: subs } = await supabase
        .from('subscriptions')
        .select('plan_name')
        .eq('user_id', user.id)
        .eq('status', 'active');

      if (subs && subs.length > 0) {
        // Prioritize Premium if exists
        const hasPremium = subs.some(s => s.plan_name === 'Premium');
        setCurrentPlan(hasPremium ? 'Premium' : subs[0].plan_name);
      } else {
        setCurrentPlan('Gratuit');
      }
    }
  };

  const plans = [
    {
      name: 'Gratuit',
      tName: t('pricing.plans.free.name'),
      subtitle: t('pricing.plans.free.subtitle'),
      price: 0,
      description: t('pricing.plans.free.description'),
      features: [
        t('pricing.plans.free.feature1'),
        t('pricing.plans.free.feature2'),
        t('pricing.plans.free.feature3'),
        t('pricing.plans.free.feature4'),
        t('pricing.plans.free.feature5'),
      ],
      accroche: t('pricing.plans.free.accroche'),
      cta: t('pricing.plans.free.cta'),
      popular: false,
    },
    {
      name: 'Standard',
      tName: t('pricing.plans.standard.name'),
      subtitle: t('pricing.plans.standard.subtitle'),
      price: 8.99,
      description: t('pricing.plans.standard.description'),
      features: [
        t('pricing.plans.standard.feature1'),
        t('pricing.plans.standard.feature2'),
        t('pricing.plans.standard.feature3'),
        t('pricing.plans.standard.feature4'),
        t('pricing.plans.standard.feature5'),
        t('pricing.plans.standard.feature6'),
      ],
      accroche: t('pricing.plans.standard.accroche'),
      cta: t('pricing.plans.standard.cta'),
      popular: true,
    },
    {
      name: 'Premium',
      tName: t('pricing.plans.premium.name'),
      subtitle: t('pricing.plans.premium.subtitle'),
      price: 14.99,
      description: t('pricing.plans.premium.description'),
      features: [
        t('pricing.plans.premium.feature1'),
        t('pricing.plans.premium.feature2'),
        t('pricing.plans.premium.feature3'),
        t('pricing.plans.premium.feature4'),
        t('pricing.plans.premium.feature5'),
        t('pricing.plans.premium.feature6'),
      ],
      accroche: t('pricing.plans.premium.accroche'),
      cta: t('pricing.plans.premium.cta'),
      popular: false,
    },
  ];

  const handlePayment = async (planName: string) => {
    try {
      setLoading(planName);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/signup?plan=' + planName.toLowerCase());
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();

      console.log('Invoking create-checkout-session with:', { planName, planType: 'monthly' });

      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          planName,
          planType: 'monthly',
        },
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
          'Accept-Language': i18n.language,
        }
      });

      console.log('Response from function:', { data, error });

      if (error) {
        console.error('Supabase Functions error:', error);
        throw error;
      }

      if (data?.error) {
        console.error('Function returned error:', data.error);
        throw new Error(data.error);
      }

      if (data?.url) {
        console.log('Redirecting to Stripe checkout:', data.url);
        window.location.href = data.url;
      } else {
        throw new Error('Aucune URL de paiement retournée par le serveur');
      }
    } catch (error: any) {
      console.error('=== ERREUR DE PAIEMENT COMPLÈTE ===');
      console.error('Error object:', error);

      let errorMessage = t('common.error.payment');

      if (error?.message) {
        errorMessage = error.message;
      }

      alert(`${errorMessage}\n\n💡 Consultez la console (F12) pour plus de détails.`);
    } finally {
      setLoading(null);
    }
  };

  const handleManagePlan = async () => {
    try {
      setLoading('manage');
      const { data: { session } } = await supabase.auth.getSession();

      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
          'Accept-Language': i18n.language,
        }
      });

      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else if (data?.error) {
        throw new Error(data.error);
      }
    } catch (error: any) {
      console.error('Error opening portal:', error);
      alert(t('common.error.portal') + (error.message || ''));
    } finally {
      setLoading(null);
    }
  };

  const handleSelectPlan = (planName: string) => {
    if (planName === 'Gratuit') {
      if (isLoggedIn) {
        navigate('/dashboard');
      } else {
        navigate('/signup');
      }
    } else {
      handlePayment(planName);
    }
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Abonnement CitoyenPro",
    "description": t('pricing.hero.description'),
    "offers": {
      "@type": "AggregateOffer",
      "lowPrice": "0",
      "highPrice": "14.99",
      "priceCurrency": "CAD"
    }
  };

  return (
    <div className="min-h-screen bg-dark-bg text-text-main">
      <SEO
        title={t('pricing.hero.title')}
        description={t('pricing.hero.description')}
        keywords="prix citoyenneté canadienne, abonnement citoyenpro, premium preparation citoyennete"
        canonical="/pricing"
        schema={schema}
      />
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b ${scrolled ? 'bg-dark-bg/95 backdrop-blur-md border-dark-border' : 'bg-transparent border-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 shrink-0">
                <img src="/logo/app-logo.png" alt="Logo" className="w-full h-full object-contain" />
              </div>
              <span className="text-2xl font-bold text-text-main tracking-tight">CitoyenPro</span>
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-4">
              <LanguageSwitcher />
              <button
                onClick={toggleTheme}
                className="p-2 rounded-xl text-text-muted hover:bg-dark-hover hover:text-text-main transition-colors"
                title={theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'}
              >
                {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              {isLoggedIn ? (
                <Link to="/dashboard" className="px-6 py-2.5 bg-primary text-white rounded-full font-bold hover:bg-primary-hover transition-colors shadow-lg">
                  {t('dashboard.nav.dashboard')}
                </Link>
              ) : (
                <>
                  <Link to="/login" className="px-6 py-2 text-text-muted hover:text-text-main transition-colors font-medium">
                    {t('landing.nav.login')}
                  </Link>
                  <Link to="/signup" className="px-6 py-2.5 bg-primary text-white rounded-full font-bold hover:bg-primary-hover transition-colors shadow-lg">
                    {t('landing.nav.start')}
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Actions */}
            <div className="flex md:hidden items-center gap-4">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-xl text-text-muted hover:bg-dark-hover hover:text-text-main transition-colors"
              >
                {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-text-main hover:bg-dark-hover rounded-xl transition-colors"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden pt-4 pb-6 px-4 absolute top-full left-0 right-0 bg-dark-bg border-b border-dark-border shadow-2xl animate-in slide-in-from-top-2">
              <div className="flex flex-col space-y-4 text-center">
                <div className="p-3">
                  <LanguageSwitcher className="w-full justify-center p-3 hover:bg-dark-hover rounded-xl" />
                </div>
                {isLoggedIn ? (
                  <Link
                    to="/dashboard"
                    className="p-3 rounded-xl bg-primary text-white font-bold shadow-lg"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('dashboard.nav.dashboard')}
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="p-3 rounded-xl hover:bg-dark-hover text-text-main font-medium flex items-center justify-center gap-3"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <User size={20} className="text-primary" />
                      {t('landing.nav.login')}
                    </Link>
                    <Link
                      to="/signup"
                      className="p-3 rounded-xl bg-primary text-white font-bold mt-2 shadow-lg"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t('landing.nav.start')}
                    </Link>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-8 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-text-main mb-6">
            {t('pricing.hero.title')}
          </h1>
          <p className="text-xl text-text-muted mb-6 max-w-3xl mx-auto">
            {t('pricing.hero.description')}
          </p>

          {/* Promo Banner */}
          {isPromoActive && (
            <div className="inline-flex flex-col md:flex-row items-center gap-3 bg-primary/10 border border-primary/20 p-4 rounded-2xl mb-8 animate-pulse shadow-sm">
              <span className="text-primary font-bold">{t('pricing.promo.banner')}</span>
              <span className="bg-primary text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-md">
                {t('pricing.promo.code_label')}
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`relative bg-dark-card rounded-3xl border transition-all hover:shadow-xl ${plan.popular ? 'border-primary shadow-primary/20 shadow-lg scale-105 z-10' : 'border-dark-border hover:border-dark-border/50'
                  }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-primary text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg whitespace-nowrap">
                      ⭐ {plan.subtitle}
                    </span>
                  </div>
                )}

                <div className="p-8">
                  {/* Header */}
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-text-main mb-1">
                      {plan.tName}
                    </h3>
                    {!plan.popular && plan.subtitle && (
                      <p className="text-sm text-primary font-semibold mb-3">{plan.subtitle}</p>
                    )}
                    <p className="text-sm text-text-muted mb-6">{plan.description}</p>

                    <div className="mb-6">
                      <div className="flex flex-col items-center">
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-5xl font-bold text-text-main">
                            {plan.price}
                          </span>
                          <span className="text-2xl text-text-main font-bold">$</span>
                          {plan.price > 0 && (
                            <span className="text-lg text-text-muted">{t('pricing.status.per_month')}</span>
                          )}
                        </div>
                        {plan.price > 0 && (
                          <div className="text-sm font-medium text-text-muted/80 mt-1 tracking-wider">
                            {t('pricing.status.tax_included')}
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={() => {
                        if (plan.name === currentPlan && plan.name !== 'Gratuit') {
                          handleManagePlan();
                        } else {
                          handleSelectPlan(plan.name);
                        }
                      }}
                      disabled={loading === plan.name || (plan.name === 'Gratuit' && currentPlan === 'Gratuit') || loading === 'manage'}
                      className={`w-full py-4 rounded-xl font-bold text-sm transition-all shadow-lg whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${plan.name === currentPlan
                        ? 'bg-dark-hover border-2 border-dark-border text-text-muted cursor-pointer hover:bg-dark-border hover:text-text-main'
                        : plan.popular
                          ? 'bg-primary text-white hover:bg-primary-hover shadow-primary/20'
                          : 'bg-dark-bg border-2 border-primary text-primary hover:bg-primary hover:text-white'
                        }`}
                    >
                      {loading === plan.name || (loading === 'manage' && plan.name === currentPlan) ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="animate-spin" size={20} />
                          {t('pricing.status.loading')}
                        </span>
                      ) : (
                        plan.name === currentPlan
                          ? (plan.name === 'Gratuit' ? t('pricing.status.current') : t('pricing.status.manage'))
                          : (() => {
                            if (!currentPlan || currentPlan === 'Gratuit') return plan.cta;
                            if (currentPlan === 'Standard' && plan.name === 'Premium') return t('pricing.status.upgrade_premium');
                            if (currentPlan === 'Premium' && plan.name === 'Standard') return t('pricing.status.downgrade_standard');
                            return plan.cta;
                          })()
                      )}
                    </button>
                  </div>

                  {/* Features */}
                  <div className="space-y-4 mb-6">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <Check className="text-primary shrink-0 mt-0.5" size={20} />
                        <span className="text-sm text-text-main leading-relaxed">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Accroche */}
                  {plan.accroche && (
                    <div className="pt-4 border-t border-dark-border">
                      <p className="text-xs text-text-muted italic leading-relaxed">
                        {plan.accroche}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-dark-bg border-t border-dark-border scroll-mt-24">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-text-main text-center mb-12">
            {t('pricing.faq.title')}
          </h2>

          <div className="space-y-6">
            {[
              { q: 'pricing.faq.q1', a: 'pricing.faq.a1' },
              { q: 'pricing.faq.q2', a: 'pricing.faq.a2' },
              { q: 'pricing.faq.q3', a: 'pricing.faq.a3' },
              { q: 'pricing.faq.q4', a: 'pricing.faq.a4' },
              { q: 'pricing.faq.q5', a: 'pricing.faq.a5' },
            ].map((faq, index) => (
              <details
                key={index}
                className="group bg-dark-card rounded-2xl p-6 border border-dark-border hover:border-primary/50 transition-all cursor-pointer open:bg-dark-hover"
              >
                <summary className="flex items-center justify-between font-semibold text-text-main text-lg cursor-pointer list-none">
                  <span>{t(faq.q)}</span>
                  <ArrowLeft className="rotate-[-90deg] group-open:rotate-90 transition-transform" />
                </summary>
                <p className="mt-4 text-text-muted leading-relaxed">{t(faq.a)}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-dark-card border-t border-dark-border py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <Link to="/" className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 shrink-0">
                  <img src="/logo/app-logo.png" alt="Logo" className="w-full h-full object-contain" />
                </div>
                <span className="text-xl font-bold text-text-main">CitoyenPro</span>
              </Link>
              <p className="text-text-muted text-sm leading-relaxed mb-6">
                {t('landing.footer.description')}
              </p>
              <div className="space-y-4">
                <p className="text-text-muted text-sm font-bold tracking-widest">{t('landing.footer.social')}</p>
                <div className="flex items-center gap-3">
                  <a
                    href="https://www.facebook.com/CitoyenPro"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-xl bg-dark-bg border border-dark-border flex items-center justify-center text-text-muted hover:text-white hover:bg-[#1877F2] hover:border-[#1877F2] transition-all duration-300 shadow-sm group"
                    title="Facebook"
                  >
                    <Facebook size={20} className="group-hover:scale-110 transition-transform" />
                  </a>
                  <a
                    href="https://www.tiktok.com/@citoyenpro"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-xl bg-dark-bg border border-dark-border flex items-center justify-center text-text-muted hover:text-white hover:bg-black hover:border-black transition-all duration-300 shadow-sm group"
                    title="TikTok"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      className="w-5 h-5 fill-current group-hover:scale-110 transition-transform"
                    >
                      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.9-.32-1.9-.39-2.81-.12-.52.13-1.03.39-1.46.73-.78.57-1.28 1.51-1.35 2.47-.03.8.31 1.6 1.01 2.01.55.33 1.25.43 1.88.24.93-.2 1.68-.96 1.94-1.87.12-.47.11-.97.11-1.45-.01-4.7-.01-9.4-.01-14.1z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-bold mb-4 text-lg text-text-main uppercase text-xs tracking-[0.2em]">{t('landing.footer.product')}</h4>
              <ul className="space-y-4 text-sm">
                <li><Link to="/#features" className="text-text-muted hover:text-primary transition-colors cursor-pointer">{t('landing.footer.product.features')}</Link></li>
                <li><Link to="/pricing" className="text-text-muted hover:text-primary transition-colors cursor-pointer">{t('landing.nav.pricing')}</Link></li>
                <li><a href="#faq" className="text-text-muted hover:text-primary transition-colors cursor-pointer">FAQ</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-4 text-lg text-text-main uppercase text-xs tracking-[0.2em]">{t('landing.footer.resources')}</h4>
              <ul className="space-y-4 text-sm">
                <li><Link to="/study-guide" className="text-text-muted hover:text-primary transition-colors cursor-pointer">{t('landing.footer.resources.guide')}</Link></li>
                <li><Link to="/help-center" className="text-text-muted hover:text-primary transition-colors cursor-pointer">{t('landing.footer.resources.help')}</Link></li>
                <li><Link to="/legal/contact" className="text-text-muted hover:text-primary transition-colors cursor-pointer">{t('landing.footer.resources.contact')}</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-4 text-lg text-text-main uppercase text-xs tracking-[0.2em]">{t('landing.footer.legal')}</h4>
              <ul className="space-y-4 text-sm">
                <li><Link to="/legal/privacy" className="text-text-muted hover:text-primary transition-colors cursor-pointer">{t('landing.footer.legal.privacy')}</Link></li>
                <li><Link to="/legal/terms" className="text-text-muted hover:text-primary transition-colors cursor-pointer">{t('landing.footer.legal.terms')}</Link></li>
                <li><Link to="/legal/notices" className="text-text-muted hover:text-primary transition-colors cursor-pointer">{t('landing.footer.legal.notices')}</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-dark-border pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-text-muted text-sm">{t('landing.footer.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
